"use client";

import type { PaletteItem } from "@/lib/commandPalette";
import { badgeClasses, renderIcon } from "./paletteIcons";

/** One row of the result list: icon, title, badge, subtitle, and the Enter hint when highlighted. */
export function PaletteResultItem({
  item,
  index,
  selected,
  disabled,
  onHover,
}: {
  item: PaletteItem;
  /** The row's position in the flat list, the one the keyboard moves through. */
  index: number;
  selected: boolean;
  disabled: boolean;
  onHover: (index: number) => void;
}) {
  return (
    <button
      type="button"
      data-index={index}
      role="option"
      aria-selected={selected}
      disabled={disabled}
      onClick={() => void item.perform()}
      onMouseEnter={() => onHover(index)}
      className={`group flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left transition ${
        selected
          ? "bg-violet-600/15 text-zinc-100 ring-1 ring-violet-500/40"
          : "text-zinc-300 hover:bg-zinc-900/60"
      }`}
    >
      <div className="flex min-w-0 items-center gap-3">
        {renderIcon(item.type, selected)}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`truncate text-sm font-medium ${
                selected ? "text-violet-200" : "text-zinc-200"
              }`}
            >
              {item.title}
            </span>
            {item.badge && (
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-medium leading-none ${badgeClasses(
                  item.badge.variant
                )}`}
              >
                {item.badge.text}
              </span>
            )}
          </div>
          {item.subtitle && (
            <p className="truncate text-xs text-zinc-500">
              {item.subtitle}
            </p>
          )}
        </div>
      </div>

      <div className="ml-3 flex shrink-0 items-center gap-2">
        {selected && (
          <span className="flex items-center gap-1 text-[11px] text-zinc-400">
            Select
            <kbd className="rounded border border-zinc-700 bg-zinc-800 px-1 py-0.5 text-[9px] font-mono">
              ↵
            </kbd>
          </span>
        )}
      </div>
    </button>
  );
}
